<template>
    <v-container>
        <oss-dialog
            v-model="dialog"
            :max-width="$vuetify.display.mobile ? 'auto' : '900px'"
            :title="'Ajouter une t&#xe2;che'"
            :previous-display="windowStep !== 'default' && windowStep !== 'taskHistory'"
            :validDisabled="!isValid"
            @valid="valid()"
            @close="close()"
            @previous="previous()"
        >
            <v-row no-gutters>
                <v-col
                    cols="12"
                    class="d-flex align-center justify-end flex-shrink-1 flex-grow-0 pl-2"
                    v-if="windowStep !== 'createTask' && windowStep !== 'usersChoice'"
                >
                    <v-btn-toggle v-model="windowStep" rounded dense color="secondary" class="pr-2">
                        <v-btn size="small" :value="'default'" icon="$fa-calendar"> </v-btn>
                        <v-btn size="small" :value="'taskHistory'" icon="$fa-history"> </v-btn>
                    </v-btn-toggle>
                    <v-btn
                        @click="
                            windowStep = 'createTask';
                            emptyTask();
                        "
                        rounded
                        elevation="0"
                        color="primary"
                        class="pl-2"
                        dark
                        size="small"
                    >
                        Créer <v-icon right>$add</v-icon>
                    </v-btn>
                </v-col>
                <v-col cols="12">
                    <v-window v-model="windowStep">
                        <v-window-item :value="'default'">
                            <UserCalendar> </UserCalendar>
                        </v-window-item>
                        <v-window-item :value="'createTask'">
                            <v-form v-model="taskValid">
                                <v-row no-gutters class="d-flex justify-center align-center">
                                    <v-col cols="12" class="d-flex pa-1">
                                        <v-text-field-filled
                                            v-model="task.title"
                                            label="Titre"
                                            outlined
                                            hide-details
                                            :rules="[(v) => !!v || $t('settings.itemRequired')]"
                                            density="compact"
                                        />
                                    </v-col>
                                    <v-col cols="12" class="d-flex pa-1">
                                        <ossCompanySelector
                                            v-model="task.company"
                                            :items="allAvailableCompanies"
                                            :selectClass="'oss-dark'"
                                            :chipMaxLength="9"
                                        />
                                    </v-col>
                                    <v-col class="d-flex pa-1" cols="8">
                                        <v-text-field-filled
                                            label="Choix Collaborateur"
                                            @click="openUsersChoice"
                                            readonly
                                            outlined
                                            :rules="[(v) => !!v || $t('settings.itemRequired')]"
                                            :model-value="selectedUsersFromComponent"
                                            density="compact"
                                        />
                                    </v-col>
                                    <v-col class="d-flex pa-1" cols="4">
                                        <oss-date-picker-filled
                                            v-model="task.dueDate"
                                            :label="'&#xc9;chéance'"
                                            :textFieldReadOnly="false"
                                            :rules="[(v) => !!v || $t('settings.itemRequired')]"
                                        />
                                    </v-col>
                                    <v-col class="d-flex pa-1" cols="12">
                                        <v-text-field-filled
                                            v-model="task.description"
                                            label="Description"
                                            outlined
                                            clearable
                                            :rules="[(v) => !!v || $t('settings.itemRequired')]"
                                            density="compact"
                                        />
                                    </v-col>
                                </v-row>
                            </v-form>
                        </v-window-item>
                        <v-window-item v-if="tasks" :value="'taskHistory'">
                            <v-row no-gutters>
                                <v-col cols="12" class="pt-2">
                                    <oss-data-table
                                        :headers="tasksTableHeaders"
                                        :items="tasks"
                                        :itemLength="tasksLength"
                                        :show-select="true"
                                        :x-dense="true"
                                        :striped="true"
                                        :table-hover="true"
                                        :fixed-footer="true"
                                        :fixed-header="true"
                                        checkbox-color="primary"
                                    >
                                        <template v-slot:item.userTaskFrom="{ item }">
                                            {{ formatUserTaskFrom(item.userTaskFrom) }}
                                        </template>
                                        <template v-slot:item.selectedUsers="{ item }">
                                            {{ formatSelectedUsers(item.selectedUsers) }}
                                        </template>
                                        <template v-slot:item.dueDate="{ item }">
                                            {{ formatDate(item.dueDate) }}
                                        </template>
                                        <template v-slot:item.company="{ item }">
                                            {{ formatCompany(item.company) }}
                                        </template>
                                    </oss-data-table>
                                </v-col>
                            </v-row>
                        </v-window-item>
                        <v-window-item :value="'usersChoice'">
                            <v-col
                                class="d-flex align-center justify-end flex-shrink-1 flex-grow-0"
                            >
                                <select-user
                                    class="px-1"
                                    v-model="selectedUsers"
                                    :companyId="companyId"
                                    :type="'NATURAL_PERSON'"
                                    :multiple="true"
                                />
                            </v-col>
                        </v-window-item>
                    </v-window>
                </v-col>
            </v-row>
        </oss-dialog>
    </v-container>
</template>

<script lang="ts">
import SelectUser from "../user/SelectUser.vue";
import UserCalendar from "./TasksCalendar.vue";
import ossCompanySelector from "../ossComponents/ossCompanySelector.vue";
import OssDataTable from "../ossComponents/ossDataTable2.vue";
import moment from "moment-timezone";
import { Component, Vue, toNative } from "vue-facing-decorator";
import { Model } from "vue-facing-decorator";
import { Watch } from "vue-facing-decorator";

@Component({
    components: {
        SelectUser,
        ossCompanySelector,
        OssDataTable,
        UserCalendar,
    },
})
export class UserTaskList extends Vue {
    @Model() value;

    taskValid = false;

    windowStep = "default";

    date = "";

    loading = false;

    task = {
        id: "",
        dueDate: "",
        title: "",
        userTaskFrom: {
            firstname: "",
            lastname: "",
        },
        description: "",
        selectedUsers: [],
        company: {
            id: "",
            name: "",
        },
    };

    tasks = [];

    selectedUsers = [];

    beforeMount() {
        this.updateSelectedCompany();
    }

    get user() {
        return this.$store.state.user;
    }

    get companyId() {
        // debugger
        return this.$store.state.selected_company.id;
    }

    emptyTask() {
        this.task = {
            id: "",
            dueDate: "",
            title: "",
            userTaskFrom: this.task.userTaskFrom,
            description: "",
            selectedUsers: [],
            company: this.task.company,
        };
        this.selectedUsers = [];
    }

    get selectedUsersFromComponent() {
        let line = "";
        this.task.selectedUsers = [];
        if (this.selectedUsers) {
            for (const taskToUser of this.selectedUsers) {
                debugger;
                this.task.selectedUsers.push(taskToUser);
                line += taskToUser.firstname + " " + taskToUser.lastname + ", ";
            }
            line = line.substring(0, line.length - 2);
        }
        this.task.userTaskFrom = this.user;

        return line;
    }

    get selectedCompany() {
        return this.$store.state.selected_company;
    }

    get allAvailableCompanies() {
        return this.$store.state.user.allAvailableCompanies;
    }

    get isValid() {
        switch (this.windowStep) {
            case "createTask":
                return this.taskValid;
            case "default":
                return false;
            case "usersChoice":
                return false;
            case "taskHistory":
                return false;
        }
        return false;
    }

    get tasksTableHeaders() {
        return [
            { text: "", value: "checkbox", sortable: false },
            { text: "", value: "", sortable: false },
            { text: "Tiers", value: "company", align: "left", sortable: false },
            { text: "Créé par", value: "userTaskFrom", align: "left", sortable: false },
            { text: "Titre", value: "title", align: "left", sortable: false },
            { text: "Description", value: "description", align: "left", sortable: false },
            { text: "Attribué à", value: "selectedUsers", align: "left", sortable: false },
            { text: "", value: "", sortable: false },
            { text: "Date d'échéance", value: "dueDate", align: "right", sortable: false },
        ];
    }

    get tasksLength() {
        if (this.tasks) {
            return this.tasks.length;
        }

        return null;
    }

    get dialog() {
        return this.value;
    }

    set dialog(event) {
        this.value = event;
    }

    async valid() {
        switch (this.windowStep) {
            case "default":
                break;
            case "createTask":
                debugger;
                console.log(this.task);
                this.tasks.push(this.task);
                this.windowStep = "default";
                break;
            case "usersChoice":
                break;
            case "taskHistory":
                break;
        }
    }

    previous() {
        switch (this.windowStep) {
            case "usersChoice":
                this.windowStep = "createTask";
                break;
            case "createTask":
                this.windowStep = "default";
                break;
            case "default":
                break;
            case "taskHistory":
                break;
        }
        // if(this.windowStep === "usersChoice"){
        //     this.windowStep = 'default'
        // }
    }

    close() {
        this.dialog = false;
        this.previous();
    }

    openUsersChoice() {
        this.windowStep = "usersChoice";
    }

    @Watch("selectedCompany", { deep: true })
    updateSelectedCompany() {
        debugger;
        this.task.company.id = this.selectedCompany.id;
        this.task.company.name = this.selectedCompany.name;
    }

    // Formatting functions for oss data table

    formatCompany(company) {
        if (company) {
            debugger;
            return company.name;
        }
        return "";
    }

    formatUserTaskFrom(userTaskFrom) {
        if (userTaskFrom) {
            return userTaskFrom.firstname + " " + userTaskFrom.lastname;
        }
    }

    formatSelectedUsers(selectedUsers) {
        if (selectedUsers) {
            debugger;
            let nbUsers = 0;
            nbUsers = selectedUsers.length - 1;
            for (const selectedUser of selectedUsers) {
                if (selectedUsers.length < 2) {
                    return selectedUser.firstname + " " + selectedUser.lastname;
                } else {
                    return (
                        selectedUsers[0].firstname +
                        " " +
                        selectedUsers[0].lastname +
                        " +" +
                        nbUsers +
                        " personne(s)"
                    );
                }
            }
        }
    }

    formatDate(dueDate) {
        if (!dueDate) return "";
        return moment(dueDate).format("DD/MM/YYYY");
    }

    // displayUsers(data){
    //     if(data.item.firstname && data.item.lastname) {
    //         return data.item.firstname + " " + data.item.lastname
    //     } else {
    //         return data.item.email
    //     }
    // }
}

export default toNative(UserTaskList);
</script>
